import {useCallback, useEffect, useState} from 'react';

import {useDispatch, useSelector, useStore} from 'react-redux';

import {deleteUserCreditCard} from '~/shared/services/bffService/api';
import {ApiError, is401Error, isApiError} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth';
import {UserGetPaymentMethodsResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {UserCreditCard, UserPaypal} from '~/personalArea/components/Sections/Payments/types';
import {trackEvent} from '~/shared/services/analytics';
import {useLocation} from '~/shared/router';
import apiService from '~/shared/services/apiService';
import {selectUser} from '~/shared/store/storeModules/user';
import {convertPathnameToAnalyticsPageSource} from '~/shared/services/analytics/utils';
import {createLogger} from '~/shared/logging';
import {AppState} from '~/shared/store/configStore';
import {selectUserPayments} from '~/shared/store/storeModules/payments/paymentsSelectors';
import {activateUserMoneycardById, getUserPayments} from '~/shared/store/storeModules/payments/paymentsActions';

const logger = createLogger('usePayments');

const paymentsWithoutMoneycards = (paymentsData: UserGetPaymentMethodsResponse): (UserCreditCard | UserPaypal)[] => {
  const cards: UserCreditCard[] = paymentsData.creditCards || [];
  const paypals: UserPaypal[] = paymentsData?.paypal?.map(p => ({...p, type: 'paypal'})) || [];

  return [...cards, ...paypals];
};

const usePayments = ({
  autoFetch = true,
}: {
  autoFetch?: boolean;
} = {}) => {
  const dispatch = useDispatch();
  const store = useStore<AppState>();
  const [error, setError] = useState<ApiError | null>(null);
  const location = useLocation();
  const user = useSelector(selectUser);

  const userPayments = useSelector(selectUserPayments);

  const moneyCards = userPayments.data?.moneyCards;

  const getUserPaymentsHandler = useCallback(async () => {
    if (!user.data) {
      return;
    }
    try {
      await dispatch(getUserPayments());
    } catch (err) {
      if (is401Error(err)) {
        await handleRefreshToken(err, getUserPaymentsHandler);
        return;
      }
      if (isApiError(err)) {
        setError(err);
      }
      if (!isApiError(err)) {
        logger.warn('Unexpected error in getPayments');
      }
    }
  }, [dispatch, user]);

  const removePaymentMethodHandler = useCallback(async (args: {
    id: number;
    type: string;
  }): Promise<boolean> => {
    try {
      const {id, type} = args;
      if (type !== 'paypal') {
        await deleteUserCreditCard(id);
      }
      if (type === 'paypal') {
        await apiService.removePayPalUserToken({paypalId: id});
      }
      trackEvent('removePaymentMethod_successful', {
        linkType: type === 'paypal' ? type : 'credit_card',
        pageSource: convertPathnameToAnalyticsPageSource(location.pathname),
      });
      getUserPaymentsHandler();
      return true;
    } catch (err) {
      if (is401Error(err)) {
        return handleRefreshToken(err, removePaymentMethodHandler, args);
      }

      return false;
    }
  }, [getUserPaymentsHandler, location.pathname]);

  useEffect(() => {
    // Read the live store state rather than the render-time `userPayments` snapshot: several hook instances
    // (e.g. header ActionMenu + TapAndSavePage) run this effect in the same commit, and only the store
    // reflects the `loading` flag set synchronously by the first instance's dispatch
    const currentUserPayments = selectUserPayments(store.getState());
    const isPaymentsInInitialState = !currentUserPayments.loading &&
      !currentUserPayments.loaded &&
      !currentUserPayments.data;

    if (autoFetch && isPaymentsInInitialState && user.loaded && !user.loading && user.data) {
      getUserPaymentsHandler();
    }
  }, [getUserPaymentsHandler, userPayments, user, autoFetch, store]);

  const tenbisCreditBalance = moneyCards?.find(card => card.isTenbisCredit)?.balance || 0;

  const activateMoneyCardById = useCallback((id: string) => {
    if (!moneyCards?.length) {
      logger.warn('No moneycards to activate');
      return;
    }

    dispatch(activateUserMoneycardById(id));
  }, [moneyCards, dispatch]);

  const hasWalletCard = moneyCards?.some(card => card.isWalletCard);

  return {
    payments: userPayments.data && paymentsWithoutMoneycards(userPayments.data),
    moneyCards,
    tenbisCreditBalance,
    isLoaded: userPayments.loaded && user.loaded && !user.loading,
    isLoading: userPayments.loading,
    activateMoneyCardById,
    removePaymentMethodHandler,
    getUserPayments: getUserPaymentsHandler,
    error,
    hasWalletCard,
  };
};

export default usePayments;
